import React, { type FunctionComponent, useEffect, useState } from 'react'
import { PublicLayout } from 'domains/public/components/layout/PublicLayout'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { useStoreSessionToken } from 'api/utils/apollo/session/sessionToken.hooks'
import { DashRedirect } from 'legacy/dash-components/dash-redirect/DashRedirect'
import { useUrlSearchParams } from 'utils/query-params/hooks/useUrlSearchParams.hooks'
import clientEnv from 'utils/global/clientEnv'
import {
  authServiceClientId,
  grantType,
  pkceCodeVerifierKey,
  pkceStateKey,
  redirectUri,
} from 'domains/public/components/auth-service/utils/constants'
import { errors } from 'utils/error-handling/errors'

export const AuthServiceCallback: FunctionComponent = () => {
  const { value: code } = useUrlSearchParams('code')
  const { value: state } = useUrlSearchParams('state')
  const [jwt, setJwt] = useState()
  const { completed } = useStoreSessionToken(jwt)

  useEffect(() => {
    if (!code || !state) {
      return
    }

    if (localStorage.getItem(pkceStateKey) !== state) {
      errors.captureMessage('State returned from auth request not matching original key', {
        level: 'warning',
      })
      return
    }

    const fetchIdToken = async (code: string) => {
      /* eslint-disable @typescript-eslint/naming-convention */
      const body = new URLSearchParams({
        grant_type: grantType,
        code: code,
        redirect_uri: redirectUri,
        client_id: authServiceClientId,
        code_verifier: localStorage.getItem(pkceCodeVerifierKey),
      })
      /* eslint-enable @typescript-eslint/naming-convention */

      const result = await fetch(`${clientEnv.PUBLIC_CLIENT_AUTH_SERVICE_BASE_URL}/oauth/token`, {
        headers: {
          'Content-type': 'application/x-www-form-urlencoded',
        },
        method: 'post',
        body,
      })

      const data = await result.json()
      setJwt(data.id_token)
    }

    fetchIdToken(code)
  }, [code, state])

  if (completed) {
    localStorage.removeItem(pkceStateKey)
    localStorage.removeItem(pkceCodeVerifierKey)

    return <DashRedirect toRoute="app-halt-redirect" />
  }

  return (
    <PublicLayout
      headerMessageId="public.authService.callback.header"
      data-testid="public-auth-service-callback"
    >
      <LoadingPage />
    </PublicLayout>
  )
}
